import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function ReservationAnnulation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('id');
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) { setError('Lien invalide.'); setLoading(false); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reservations/${id}`)
      .then(res => { setReservation(res.data); setLoading(false); })
      .catch(() => { setError('Reservation introuvable.'); setLoading(false); });
  }, [id]);

  const handleAnnuler = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/cancel-reservation/${id}`);
      setDone(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'annulation.");
    }
    setConfirming(false);
  };

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const heuresAvant = reservation
    ? (new Date(`${reservation.date}T${reservation.heure_debut}`) - new Date()) / (1000 * 60 * 60)
    : null;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Chargement...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <p className="text-red-600 font-medium mb-4">{error}</p>
        <button onClick={() => navigate('/')} className="text-accent hover:underline text-sm">
          Retour a l accueil
        </button>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-3xl">✓</span>
        </div>
        <h1 className="text-2xl font-serif mb-3">Reservation annulee</h1>
        <p className="text-muted-foreground mb-2">{done.message}</p>
        <button onClick={() => navigate('/')} className="mt-6 text-accent hover:underline text-sm">
          Retour a l accueil
        </button>
      </div>
    </div>
  );

  if (reservation.statut === 'cancelled') return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <p className="text-muted-foreground mb-4">Cette reservation est deja annulee.</p>
        <button onClick={() => navigate('/')} className="text-accent hover:underline text-sm">
          Retour a l accueil
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-secondary/20">
      <div className="bg-white rounded-sm shadow-sm border border-border max-w-md w-full p-8">
        <h1 className="text-2xl font-serif mb-2 text-center">Annuler ma reservation</h1>
        <p className="text-muted-foreground text-sm text-center mb-8">
          Veuillez confirmer l annulation de votre rendez-vous.
        </p>

        <div className="border border-border rounded-sm p-4 mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Prestation</span>
            <span className="font-medium">{reservation.prestation_nom}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Date</span>
            <span className="font-medium">{formatDate(reservation.date)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Horaire</span>
            <span className="font-medium">{reservation.heure_debut?.substring(0,5)} - {reservation.heure_fin?.substring(0,5)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Montant total</span>
            <span className="font-medium">{reservation.montant_total} EUR</span>
          </div>
        </div>

        {heuresAvant < 48 ? (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 text-sm text-red-800">
            <strong>Attention :</strong> Le rendez-vous est dans moins de 48h. L acompte de <strong>{reservation.montant_acompte} EUR</strong> sera debite.
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-6 text-sm text-green-800">
            <strong>Annulation gratuite</strong> — Le rendez-vous est dans plus de 48h, aucun frais ne sera applique.
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleAnnuler}
            disabled={confirming}
            className="w-full py-3 bg-red-600 text-white rounded-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {confirming ? 'Annulation en cours...' : "Confirmer l'annulation"}
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 border border-border rounded-sm text-sm text-muted-foreground hover:bg-secondary"
          >
            Garder mon rendez-vous
          </button>
        </div>
      </div>
    </div>
  );
}
